import {
  Box, Card, CardHeader, Skeleton,
} from '@mui/material';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Chart, { useChart, ApexOptions } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { AnimatedDivider } from '../../../../components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getIsOrdersLoading } from '../../../../store/orders/selectors';

type TTeamHoursWorkedBarChart = {
  team: {
    id: number;
    name: string;
    members: Array<{
      name: string;
      email: string;
      totalHoursWorked: number;
    }>;
  };
};

const TeamHoursWorkedBarChart: FC<TTeamHoursWorkedBarChart> = ({ team }) => {
  const { t } = useTranslation(['commissions', 'common', 'inputs', 'timesheets']);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);
  const chartRef = useRef<any>(null);

  const chartData = useMemo(() => {
    const categories = team.members.map((member) => member.name || 'Unknown');
    const seriesData = team.members.map((member) => member.totalHoursWorked || 0);
    return { categories, seriesData };
  }, [team]);

  const chartOptions: ApexOptions = useChart({
    chart: {
      type: 'bar',
      events: {
        mounted: (chart) => {
          chartRef.current = chart;
        },
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: { text: t('hoursWorked', { ns: 'timesheets' }) },
      labels: {
        formatter: (value: number) => `${value.toFixed(1)}h`,
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
      y: {
        formatter: (value: number) => `${value.toFixed(1)} ${t('hours', { ns: 'inputs' })}`,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '40%',
        distributed: true,
      },
    },
  });

  const validSeries = [
    {
      name: t('hoursWorked', { ns: 'timesheets' }),
      data: chartData.seriesData,
    },
  ];

  return (
    <Card>
      <CardHeader
        sx={{ p: 2 }}
        title={t('employeeHoursWorked')}
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <Box sx={{ mx: 3 }} dir="ltr">
        {/* eslint-disable-next-line no-nested-ternary */}
        {isOrdersLoading ? (
          <Box pt={3}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i?.toString()} variant="rectangular" height={40} sx={{ mb: 2 }} />
            ))}
          </Box>
        ) : chartData.categories.length ? (
          <Chart
            type="bar"
            series={validSeries}
            options={chartOptions}
            height={400}
          />
        ) : (
          <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />
        )}
      </Box>
    </Card>
  );
};

export { TeamHoursWorkedBarChart };
